import { useState } from "react";
import { CheckCircle2, CircleOff, Loader2, PlugZap, Facebook } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";

export const NETWORK_LABELS = { discord: "Discord", instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };

const MetaCheck = () => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.get("/networks/meta/check")).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid="meta-check-panel">
      <button onClick={run} disabled={busy} data-testid="meta-check-button"
        className="flex items-center gap-2 px-3 py-2 text-sm dz-heading uppercase tracking-wider font-bold border border-white/15 text-zinc-300 hover:text-white">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />} Testar conexão Meta
      </button>
      {res && ["facebook", "instagram"].map((n) => (
        <p key={n} className={`text-xs mt-2 ${res[n].ok ? "text-emerald-400" : "text-amber-300"}`} data-testid={`meta-check-${n}`}>
          {NETWORK_LABELS[n]}: {res[n].message}
        </p>
      ))}
      <p className="text-xs text-zinc-500 mt-3">
        Para o Instagram: a conta precisa ser Profissional (Empresa ou Criador) e estar vinculada à Página do Facebook.
      </p>
    </div>
  );
};

const MetaPagePicker = ({ onSaved }) => {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = (await api.get("/networks/meta/pages")).data;
      setData(r);
      setChoice(r.selected && r.pages.some((p) => p.id === r.selected) ? r.selected : "");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try {
      const r = (await api.put("/networks/meta/page", { page_id: choice })).data;
      toast.success(`Página escolhida: ${r.page_name}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4 border-t border-white/10 pt-4 space-y-3" data-testid="meta-page-picker">
      <p className="text-xs text-zinc-400">Página do Facebook que recebe os posts (o token da Página é obtido automaticamente).</p>
      {!data ? (
        <button onClick={load} disabled={busy} data-testid="load-meta-pages-button"
          className="flex items-center gap-2 px-3 py-2 text-sm dz-heading uppercase tracking-wider font-bold border border-white/15 text-zinc-300 hover:text-white">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Facebook size={15} />} Escolher Página
        </button>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger className="rounded-none bg-black/40 border-white/15 text-white sm:w-80" data-testid="meta-page-select">
              <SelectValue placeholder="Selecione a Página" />
            </SelectTrigger>
            <SelectContent>
              {data.pages.map((p) => (
                <SelectItem key={p.id} value={p.id} disabled={!p.can_post} data-testid={`meta-page-option-${p.id}`}>
                  {p.name}{p.instagram ? ` · IG @${p.instagram}` : ""}{!p.can_post ? " (sem permissão para postar)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={save} disabled={!choice} data-testid="save-meta-page-button"
            className="px-4 py-2 bg-[#c81e1e] text-white dz-heading uppercase font-bold tracking-wider disabled:opacity-40">Salvar Página</button>
        </div>
      )}
      {data && !data.pages.length && <p className="text-xs text-amber-300">Nenhuma Página encontrada para este token.</p>}
    </div>
  );
};

export const NetworkStatus = ({ networks, isAdmin, onChanged }) => {
  const pageName = networks.find((n) => n.network === "facebook")?.page_name;
  return (
    <div className="lg:col-span-2 border border-white/10 bg-[#121314] p-5" data-testid="network-status-panel">
      <p className="dz-heading text-lg font-bold uppercase text-white mb-4">Redes sociais</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {networks.map((n) => (
          <div key={n.network} className={`border p-3 ${n.connected ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10"}`}
            data-testid={`network-${n.network}-status`}>
            <p className="dz-heading font-bold uppercase text-white">{NETWORK_LABELS[n.network]}</p>
            <p className={`mt-1 text-xs flex items-center gap-1.5 ${n.connected ? "text-emerald-400" : "text-zinc-500"}`}>
              {n.connected ? <CheckCircle2 size={13} /> : <CircleOff size={13} />}
              {n.connected ? "Credenciais salvas" : "Não conectado"}
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-4">
        {pageName ? <>Página do Facebook: <span className="text-zinc-200" data-testid="meta-page-name">{pageName}</span>. </> : null}
        Redes não conectadas são ignoradas na publicação. Use "Testar conexão Meta" para confirmar que Facebook e Instagram podem publicar.
      </p>
      {isAdmin && <MetaPagePicker onSaved={onChanged} />}
      <MetaCheck />
    </div>
  );
};
